import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { authApi, organizationApi } from '@/lib/pillarApi';
import { StatusBadge } from '@/components/ui/status-badge';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import type { ActiveRoleAssignmentResponse } from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import type {
    OrganizationCapacityKey,
    OrganizationOmnisearchListItem,
    OrganizationOmnisearchPageResponse,
} from '@pillar-portal/api-types';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { getOrganizationCapacityFilter } from '@/lib/organization-capacity';

interface OrganizationsListPageProps {
    organizationCapacityKey: OrganizationCapacityKey;
    title: string;
    cardTitle: string;
    createPath?: string;
    createButtonLabel?: string;
    queryKeyPrefix: ReadonlyArray<string>;
}

function buildMerchantAccountTupleText(
    organization: OrganizationOmnisearchListItem
): ReadonlyArray<string> {
    if (organization.merchant_accounts === undefined || organization.merchant_accounts.length === 0) {
        return ['No merchant accounts'];
    }
    return organization.merchant_accounts.map(
        (merchantAccount) =>
            merchantAccount.surcharge_enabled
                ? `MID: ${merchantAccount.processor_mid}, surcharge $${merchantAccount.surcharge_pct}%`
                : `MID: ${merchantAccount.processor_mid}, no surcharge`
    );
}

export function OrganizationsListPage(props: OrganizationsListPageProps) {
    const navigate = useNavigate();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [...props.queryKeyPrefix, `orgs=${organizationListsVersion}`],
        [organizationListsVersion, props.queryKeyPrefix]
    );

    const { data: activeRoleAssignment, isLoading: isActiveRoleAssignmentLoading } =
        useQuery<ActiveRoleAssignmentResponse | null>({
            queryKey: ['activeRoleAssignment'],
            queryFn: async () => {
                return await authApi.getActiveRoleAssignment();
            },
            staleTime: 30000,
        });

    const canCreateOrganizations: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment ?? null
    );

    if (isActiveRoleAssignmentLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<OrganizationOmnisearchListItem, string, ReadonlyArray<string>>
            pageTitle={props.title}
            cardTitle={props.cardTitle}
            queryPlaceholder="Search..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            fetchPage={async (params) => {
                const response: OrganizationOmnisearchPageResponse =
                    await organizationApi.searchOrganizations({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        ...getOrganizationCapacityFilter(props.organizationCapacityKey),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                return { items: response.organizations, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().trim().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected opaque cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading organizations...',
                emptyLabel: 'No organizations found.',
                endOfListLabel: 'End of organizations.',
                genericErrorLabel: 'Failed to load organizations.',
            }}
            headerActions={
                canCreateOrganizations &&
                props.createPath !== undefined &&
                props.createButtonLabel !== undefined ? (
                    <Button onClick={() => navigate(props.createPath!)}>
                        {props.createButtonLabel}
                    </Button>
                ) : undefined
            }
            renderItems={({ items }) => {
                return (
                    <div className="space-y-2">
                        {items.map((org: OrganizationOmnisearchListItem) => (
                            <button
                                key={org.organization_uuid}
                                type="button"
                                className="w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                                onClick={() => navigate(`/organizations/${org.organization_uuid}`)}
                            >
                                <div className="flex justify-between items-start gap-8">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">{org.name}</h3>
                                            <StatusBadge status={org.status} />
                                        </div>
                                        {org.businessDetails !== undefined && (
                                            <div className="mt-1 space-y-0.5">
                                                <p className="text-xs text-muted-foreground">
                                                    {org.businessDetails.legalEntityName}
                                                </p>
                                                {org.businessDetails.dba !== undefined && (
                                                    <p className="text-xs text-muted-foreground">
                                                        DBA: {org.businessDetails.dba}
                                                    </p>
                                                )}
                                                {org.businessDetails.department !== undefined && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Department: {org.businessDetails.department}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <p className="text-xs font-medium text-muted-foreground">
                                            Merchant accounts
                                        </p>
                                        <div className="text-xs text-muted-foreground text-right space-y-0.5">
                                            {buildMerchantAccountTupleText(org).map((tupleText: string) => (
                                                <p key={tupleText}>{tupleText}</p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                );
            }}
        />
    );
}
